/**
 * End-to-end payload encryption for calls to the MaxelPay API.
 *
 * Counterpart of the backend's services/e2e.service.js -- keep the two in step.
 *
 * The browser holds only the server's PUBLIC key. For every API request it
 * makes a one-off P-256 key pair, agrees a secret with the server (ECDH), and
 * derives two AES-256-GCM keys from it (HKDF-SHA256, salt = nonce): one to
 * encrypt the request body, one to decrypt the response. Nothing secret ships
 * in the bundle.
 *
 * installSecureFetch() wraps window.fetch once at startup, so RTK Query,
 * fetchWithAuth and the pay pages' direct fetch calls are all covered without
 * touching each call site. Only URLs under REACT_APP_API_URL are affected;
 * wallet RPCs, images and third-party calls pass straight through.
 *
 * Falls back to plain JSON over TLS when WebCrypto is unavailable (an http://
 * page that is not localhost) or the server has no key configured. In
 * development it can be switched off from the console to read payloads in
 * DevTools:  localStorage.setItem('maxelpay:e2e', 'off')
 */

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/+$/, '');
const PUBLIC_KEY_URL = `${API_BASE}/e2e/public-key`;
const HEADER = 'X-MP-E2E';
const INFO_REQ = 'maxelpay-e2e-v1 req';
const INFO_RES = 'maxelpay-e2e-v1 res';
const KEY_RETRY_MS = 60 * 1000;

const enc = new TextEncoder();
const dec = new TextDecoder();

let nativeFetch = null;
let keyState = null; // { promise, at }

const subtle = () => globalThis.crypto?.subtle;

const b64url = {
  encode(bytes) {
    let bin = '';
    new Uint8Array(bytes).forEach((b) => {
      bin += String.fromCharCode(b);
    });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  },
  decode(str) {
    const b64 = String(str).replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  },
};

const isDisabled = () => {
  if (process.env.REACT_APP_E2E === 'off') return true;
  if (process.env.NODE_ENV === 'production') return false;
  try {
    return globalThis.localStorage?.getItem('maxelpay:e2e') === 'off';
  } catch {
    return false;
  }
};

const absolute = (url) => {
  try {
    return new URL(url, globalThis.location?.href).href;
  } catch {
    return String(url);
  }
};

const isApiUrl = (url) => {
  const href = absolute(url);
  const base = absolute(API_BASE);
  return href.startsWith(`${base}/`) && !href.startsWith(absolute(PUBLIC_KEY_URL));
};

// --------------------------------------------------------------------------
// Server key
// --------------------------------------------------------------------------

const loadServerKey = async () => {
  const res = await nativeFetch(PUBLIC_KEY_URL, { credentials: 'omit' });
  if (!res.ok) return null;
  const { data } = await res.json();
  if (!data?.publicKey || data.version !== 'v1') return null;
  const publicKey = await subtle().importKey(
    'raw',
    b64url.decode(data.publicKey),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );
  // Clock skew correction: the server refuses requests more than 2 min off its
  // own clock, and plenty of laptops are further off than that.
  return { kid: data.kid, publicKey, offset: Number(data.serverTime) - Date.now() || 0 };
};

const getServerKey = ({ refresh = false } = {}) => {
  const now = Date.now();
  const failedRecently = keyState && keyState.failed && now - keyState.at < KEY_RETRY_MS;
  if (!refresh && keyState && (!keyState.failed || failedRecently)) return keyState.promise;

  const state = { at: now, failed: false };
  state.promise = loadServerKey()
    .catch(() => null)
    .then((key) => {
      if (!key) state.failed = true;
      return key;
    });
  keyState = state;
  return state.promise;
};

// --------------------------------------------------------------------------
// Per-request session
// --------------------------------------------------------------------------

/** Build the X-MP-E2E header and the two AES keys for one request. */
export const openSession = async (serverKey) => {
  const s = subtle();
  const eph = await s.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  const epk = await s.exportKey('raw', eph.publicKey);
  const shared = await s.deriveBits({ name: 'ECDH', public: serverKey.publicKey }, eph.privateKey, 256);
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(16));

  const hkdf = await s.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  const derive = (info, usage) =>
    s.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: nonce, info: enc.encode(info) },
      hkdf,
      { name: 'AES-GCM', length: 256 },
      false,
      [usage]
    );
  const [reqKey, resKey] = await Promise.all([derive(INFO_REQ, 'encrypt'), derive(INFO_RES, 'decrypt')]);

  const header = [
    'v1',
    serverKey.kid,
    b64url.encode(epk),
    String(Date.now() + serverKey.offset),
    b64url.encode(nonce),
  ].join('.');
  const aad = enc.encode(header);

  return {
    header,
    async encrypt(text) {
      const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
      const ct = await s.encrypt({ name: 'AES-GCM', iv, additionalData: aad }, reqKey, enc.encode(text));
      return JSON.stringify({ enc: 1, iv: b64url.encode(iv), ct: b64url.encode(ct) });
    },
    async decrypt(envelope) {
      const plain = await s.decrypt(
        { name: 'AES-GCM', iv: b64url.decode(envelope.iv), additionalData: aad },
        resKey,
        b64url.decode(envelope.ct)
      );
      return dec.decode(plain);
    },
  };
};

const isEnvelope = (v) => !!v && typeof v === 'object' && v.enc === 1 && typeof v.iv === 'string' && typeof v.ct === 'string';

const isJson = (headers) => (headers.get('content-type') || '').toLowerCase().includes('application/json');

/** Replace an encrypted JSON response with its plaintext. Others are returned as-is. */
const decryptResponse = async (response, session) => {
  if (!isJson(response.headers)) return response;
  const text = await response.clone().text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return response;
  }
  if (!isEnvelope(parsed)) return response; // refusals from the E2E layer itself are plain

  const plain = await session.decrypt(parsed);
  const headers = new Headers(response.headers);
  headers.delete('content-length');
  headers.delete('content-encoding');
  return new Response(plain, { status: response.status, statusText: response.statusText, headers });
};

const sendEncrypted = async (request, serverKey) => {
  const session = await openSession(serverKey);
  const headers = new Headers(request.headers);
  headers.set(HEADER, session.header);

  const init = { headers };
  const method = request.method.toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && isJson(request.headers)) {
    const text = await request.clone().text();
    if (text) init.body = await session.encrypt(text);
  }
  // Multipart / binary bodies are sent as they are; the response is still
  // encrypted, because the header is present.
  const response = await nativeFetch(new Request(request.clone(), init));
  return decryptResponse(response, session);
};

/** fetch() with E2E encryption for API URLs. Same signature as fetch. */
export const secureFetch = async (input, init) => {
  if (!nativeFetch) nativeFetch = globalThis.fetch.bind(globalThis);
  if (typeof Request === 'undefined' || !subtle() || isDisabled()) return nativeFetch(input, init);

  const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
  if (!url || !isApiUrl(url)) return nativeFetch(input, init);

  const serverKey = await getServerKey();
  if (!serverKey) return nativeFetch(input, init);

  const request = new Request(input, init);
  const response = await sendEncrypted(request, serverKey);

  // Server key rotated since we cached it: refetch once and retry.
  if (response.status === 409) {
    const body = await response.clone().json().catch(() => null);
    if (body?.code === 'E2E_STALE_KEY') {
      const fresh = await getServerKey({ refresh: true });
      if (fresh) return sendEncrypted(request, fresh);
      return nativeFetch(request);
    }
  }
  return response;
};

/** Route window.fetch through secureFetch. Call once, before the app renders. */
export const installSecureFetch = () => {
  if (typeof window === 'undefined' || window.fetch?.maxelpayE2E) return;
  nativeFetch = window.fetch.bind(window);
  const wrapped = (input, init) => secureFetch(input, init);
  wrapped.maxelpayE2E = true;
  window.fetch = wrapped;
};

export default secureFetch;
